import { useRecoilValue, useSetRecoilState } from 'recoil';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { StructureSet } from '~/domain/patient/structureset';
import { ImportResult } from '~/domain/services/dicomProcessor';
import { EnvironmentObjects } from '~/engine/data/EnvironmentObjects';
import { currentDoseState } from '~/state/atoms/dose/doseAtoms';
import { environmentState } from '~/state/atoms/engineAtoms';
import {
    blendImageState,
    primaryImageState,
    selectedRegistrationState,
} from '~/state/atoms/image/imageAtoms';
import {
    patientRepositoryState,
    selectedPatientIdState,
} from '~/state/atoms/patientAtoms';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { reviewToolState, selectedToolState } from '~/state/atoms/toolsAtoms';
import { handleError } from '~/utils/errors';
import useWindowLevel from './useWindowLevel';

export default function useViewportEnvironment() {
    const environment = useRecoilValue(environmentState);
    const patientRepository = useRecoilValue(patientRepositoryState);
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const setSelectedPatientId = useSetRecoilState(selectedPatientIdState);
    const setPrimaryImage = useSetRecoilState(primaryImageState);
    const setBlendImage = useSetRecoilState(blendImageState);
    const setCurrentStructureSet = useSetRecoilState(currentStructureSetState);
    const setCurrentDose = useSetRecoilState(currentDoseState);
    const setCurrentRegistration = useSetRecoilState(selectedRegistrationState);
    const setSelectedROI = useSetRecoilState<number>(selectedROIState);
    const setTool = useSetRecoilState(selectedToolState);
    const setReviewStructuresOpen = useSetRecoilState(reviewToolState);
    const { setUpWindowLevelContext } = useWindowLevel();

    const searchPatient = (patientId?: string) => {
        if (!patientId) return;
        const patientToOpen = patientRepository
            .getAll()
            .find((patient) => patient.dataSet.PatientID === patientId);
        return patientToOpen;
    };

    const setEnvironmentObjects = (environmentObjects?: EnvironmentObjects) => {
        setPrimaryImage(environmentObjects?.primaryImage?.parent);
        setCurrentStructureSet(environmentObjects?.structureSet?.parent);
        setBlendImage(environmentObjects?.blendImage?.parent);
        setCurrentDose(environmentObjects?.dose?.parent);
        setCurrentRegistration(environmentObjects?.registration);
    };

    const setEnvironmentDose = (doses: DoseMatrix[], patientId: string) => {
        const patient = patientRepository.getByPatientId(patientId);
        const dose = doses.find((dose) =>
            patient?.getDoseWithDicomUid(dose.dicomUid)
        );
        if (environment && dose) {
            setSelectedPatientId(patientId);
            const environmentObjects = environment.setDose(dose);
            setEnvironmentObjects(environmentObjects);
        }
    };

    const unloadDose = () => {
        if (environment) {
            environment.setDose(undefined);
            setCurrentDose(undefined);
        }
    };

    const setEnvironmentStructureSet = (
        structureSets: StructureSet[],
        patientId: string
    ) => {
        const patient = patientRepository.getByPatientId(patientId);
        const structureSet = structureSets.find((struct) =>
            patient?.getStructureSetWithSopInstanceUid(struct.dicomUid)
        );
        if (environment && structureSet) {
            const environmentObjects =
                environment.setStructureSet(structureSet);
            if (environmentObjects) {
                cleanupStructureSetContext();
                setSelectedPatientId(patientId);
                setEnvironmentObjects(environmentObjects);
            }
        }
    };

    const unloadStructureSet = () => {
        if (environment) {
            environment.setStructureSet(undefined);
            cleanupStructureSetContext();
        }
    };

    const cleanupStructureSetContext = () => {
        setCurrentStructureSet(undefined);
        setSelectedROI(-1);
        setTool('pan');
        setReviewStructuresOpen(false);
        unloadDose();
    };

    const setEnvironmentPrimaryImage = (
        images: Image3D[],
        patientId: string
    ) => {
        const patient = patientRepository.getByPatientId(patientId);
        const image = images.find((img) =>
            patient?.getImageWithDicomUid(img.dicomUid)
        );
        if (environment && image) {
            setSelectedPatientId(patientId);
            setUpWindowLevelContext(image);
            const environmentObjects = environment.setImage(image);
            setEnvironmentObjects(environmentObjects);
        }
    };

    const removePrimaryImage = () => {
        if (environment) {
            environment.setImage(undefined);
            setPrimaryImage(undefined);
            removeEnvironmentBlendImage();
            cleanupStructureSetContext();
        }
    };

    const setEnvironmentBlendImage = (
        image: Image3D,
        registration?: RigidRegistration
    ) => {
        if (environment && image) {
            const environmentObjects = environment.setBlendImage(
                image,
                registration
            );
            setEnvironmentObjects(environmentObjects);
        }
    };

    const removeEnvironmentBlendImage = () => {
        if (environment) {
            environment.setBlendImage(undefined);
            setBlendImage(undefined);
        }
    };

    const resolvePatient = (importResult: ImportResult) => {
        try {
            let patient;
            if (importResult.images[0]) {
                const sliceData = importResult.images[0]?.firstSlice;
                patient = searchPatient(sliceData.datasetWrapper.PatientID);
            } else if (importResult.structureSets[0]) {
                patient = searchPatient(
                    importResult.structureSets[0].dicomStructureSet
                        .datasetWrapper.PatientID
                );
            } else if (importResult.doses[0]) {
                patient = searchPatient(
                    importResult.doses[0].structureSet?.dicomStructureSet
                        ?.datasetWrapper.PatientID
                );
            }

            return patient ? patient.dataSet.PatientID : selectedPatientId;
        } catch (error) {
            handleError(
                error,
                'Error when trying to resolve patient data from imported object.'
            );
        }
    };

    const initializeEnvironment = async (importResult: ImportResult) => {
        const { images, doses, structureSets, patients } = importResult;
        const patientId = patients[0]
            ? importResult.patients[0].PatientID
            : resolvePatient(importResult);

        if (patientId) {
            setSelectedPatientId(patientId);
            if (images.length > 0) {
                await setEnvironmentPrimaryImage(images, patientId);
            }
            if (structureSets.length > 0) {
                await setEnvironmentStructureSet(structureSets, patientId);
            }
            if (doses.length > 0) {
                await setEnvironmentDose(doses, patientId);
            }
        }
    };

    const environmentObjectIds = environment?.getEnvironmentObjectIds;

    return {
        environment,
        environmentObjectIds,
        initializeEnvironment,
        setEnvironmentDose,
        unloadDose,
        setEnvironmentStructureSet,
        unloadStructureSet,
        setEnvironmentPrimaryImage,
        removePrimaryImage,
        setEnvironmentBlendImage,
        removeEnvironmentBlendImage,
    };
}
